import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Edit,
  SimpleForm,
  TextInput,
  FileInput,
  BooleanInput,
  ImageField,
  useUpdate,
  useNotify,
  useRedirect,
  Toolbar,
  SaveButton,
  required,
  ImageInput,
} from 'react-admin';

import { RichTextInput } from 'ra-input-rich-text';

import { htmlToBlocks, blocksToHtml } from '../../../helpers/htmlUtils';
import { uploadImage } from '../../../config/services/strapi/strapi';

function CasosExitoEdit(props: any) {
  const [imagesChanged, setImagesChanged] = useState({
    banner: false,
    portada: false,
    portada_carousel: false,
    imagenes: false,
  });

  const { id } = useParams();

  const [update] = useUpdate();
  const redirect = useRedirect();
  const notify = useNotify();

  const processImages = async(images: any, token: string) => {
    try {
      const result: Record<string, any> = {}

      if (imagesChanged.portada && images.portada?.rawFile) {
        result.portada = await uploadImage(images.portada.rawFile, token);
      }

      if (imagesChanged.banner && images.banner?.rawFile) {
        result.banner = await uploadImage(images.banner.rawFile, token);
      }

      if (imagesChanged.portada_carousel && images.portada_carousel?.rawFile) {
        result.portada_carousel = await uploadImage(images.portada_carousel.rawFile, token);
      }

      if (imagesChanged.imagenes) {
        result.imagenes = await Promise.all(
          images.imagenes.map(async (image: any) => {
            if (image.rawFile) {
              return uploadImage(image.rawFile, token);
            }

            return image; // Si no hay archivo, retornar el objeto original
          })
        );
      }

      // Solo eliminar propiedades si existen
      if (result.portada?.documentId) delete result.portada.documentId;
      if (result.banner?.documentId) delete result.banner.documentId;
      if (result.portada_carousel?.documentId) delete result.portada_carousel.documentId;

      return result;
    }
    catch(error) {
      console.error('Error procesando imágenes:', error);
      notify('Error al procesar las imágenes', { type: 'error' });
    }
  }

  const handleSubmit = async(data: any) => {
    try {
      const token = localStorage.getItem('strapi_jwt');
      if (!token) {
        notify('No se encontró el token de autenticación', { type: 'error' });
        return;
      }

      const payload = {
        ...data,
        impactos: typeof data.impactos === 'string' ? htmlToBlocks(data.impactos) : data.impactos,
        descripcion: typeof data.descripcion === 'string' ? htmlToBlocks(data.descripcion) : data.descripcion,
      }

      const images = await processImages(data, token);

      Object.assign(payload, images);

      delete payload.id; // Asegurarse de no enviar el ID en el payload
      delete payload.createdAt;
      delete payload.updatedAt;
      delete payload.publishedAt;
      delete payload.documentId;
      delete payload.ref;
      delete payload.createdAt;
      delete payload.updatedAt;
      delete payload.publishedAt;

      update(
        'casos-exitos',
        { data: payload, id: id },
        {
          onSuccess: () => {
            notify('Elemento actualizado');
            redirect('list', 'casos-exitos');
          },
          onError: (error: any) => {
            notify(`Error: ${error.message}`, { type: 'error' });
          },
        }
      );
    }
    catch (error) {
      console.error('Error al actualizar el caso de éxito:', error);
      notify('Error al actualizar el caso de éxito', { type: 'error' });
    }
  }

  useEffect(() => {
    document.title = 'Editar Caso de Éxito';
  }, []);

  return (
    <Edit {...props}>
      <SimpleForm onSubmit={handleSubmit} toolbar={<Toolbar><SaveButton alwaysEnable /></Toolbar>}>
        <TextInput source="id" disabled />
        <TextInput source="nombre" validate={[required()]} />
        <FileInput
          source='portada'
          label='Imagen de Portada'
          onChange={() => setImagesChanged({ ...imagesChanged, portada: true })}
        >
          <>
            {imagesChanged.portada && <ImageField source="src" title="title" />}
            {!imagesChanged.portada && <ImageField source="url" title="title" />}
          </>
        </FileInput>
        <FileInput
          source='banner'
          label='Imagen de Banner'
          validate={[required()]}
          onChange={() => setImagesChanged({ ...imagesChanged, banner: true })}
        >
          <>
            {imagesChanged.banner && <ImageField source="src" title="title" />}
            {!imagesChanged.banner && <ImageField source="url" title="title" />}
          </>
        </FileInput>
        <FileInput
          source='portada_carousel'
          label='Imagen de Portada Carousel'
          onChange={() => setImagesChanged({ ...imagesChanged, portada_carousel: true })}
        >
          <>
            {imagesChanged.portada_carousel && <ImageField source="src" title="title" />}
            {!imagesChanged.portada_carousel && <ImageField source="url" title="title" />}
          </>
        </FileInput>
        <TextInput source="rubro" />
        <TextInput source="region" />
        <TextInput source="provincia" />
        <TextInput source="comuna" />
        <RichTextInput source="impactos" format={blocksToHtml} parse={htmlToBlocks} />
        <RichTextInput source="descripcion" format={blocksToHtml} parse={htmlToBlocks} />
        <ImageInput
          source='imagenes'
          label='Imágenes Adicionales'
          multiple={true}
          onChange={() => setImagesChanged({ ...imagesChanged, imagenes: true })}
          validate={[required()]}
          format={(value) =>
            Array.isArray(value)
              ? value.map((image) => ({
                  src: image.url,
                  title: image.name,
                  ...image,
                }))
              : []
          }
        >
          <ImageField source="src" title="title" />
        </ImageInput>
        <TextInput source="fragmento_url" label='URL del Fragmento' />
        <TextInput source="slug" validate={[required()]} />
        <BooleanInput source="destacado" />
      </SimpleForm>
    </Edit>
  )
}

export default CasosExitoEdit